import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isStaleChunkError } from '../lib/lazyPage';
import { StaleVersionScreen } from './StaleVersionScreen';

/**
 * Jedna výnimka v rendere zhasne v Reacte celý strom — používateľ vidí bielu
 * stránku a nemá čo urobiť. Toto je posledná záchyta: appka sa nezachráni, ale
 * povie, čo sa stalo, a ponúkne cestu von.
 *
 * Zámerne trieda a nie hook: React nemá funkčný ekvivalent
 * componentDidCatch, toto je jediný spôsob, ako sa k tomu dostať.
 */
type Props = { children: ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Do konzoly, nie na server: chyba v rendere môže niesť obsah obrazovky
    // a posielať ju automaticky by znamenalo posielať dáta, o ktorých nevieme.
    console.error('[render-error]', error, info.componentStack);
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    // Toto nie je pokazená obrazovka, len stará generácia appky — a "Skúsiť
    // znova" by tu nepomohlo, lebo súbor, po ktorom appka siaha, na serveri
    // naozaj nie je. Vlastná obrazovka ponúka to jediné, čo pomôže.
    if (isStaleChunkError(error)) {
      return <StaleVersionScreen />;
    }

    return (
      <div className="flex min-h-screen items-center justify-center p-5">
        <div className="panel w-full max-w-md p-6 text-center">
          <h1 className="t-title">Toto sa pokazilo</h1>
          <p className="mt-2 text-[15px] text-slate-500 dark:text-slate-400">
            Obrazovku sa nepodarilo vykresliť. Tvoje dáta sú v poriadku — chyba je na strane
            zobrazenia.
          </p>

          {/* Text chyby je pre teba, keď to budeš hlásiť. Nie je to omluva, je
              to informácia, ktorá inak skončí len v konzole. */}
          <p className="mt-3 break-words rounded-xl bg-black/[0.04] p-3 text-left font-mono text-[12px] text-slate-500 dark:bg-white/[0.06] dark:text-slate-400">
            {error.message || 'Neznáma chyba'}
          </p>

          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <button type="button" className="btn-primary flex-1" onClick={this.reset}>
              Skúsiť znova
            </button>
            <button
              type="button"
              className="btn-secondary flex-1"
              onClick={() => {
                window.location.href = '/';
              }}
            >
              Na úvod
            </button>
          </div>
        </div>
      </div>
    );
  }
}
